/**
 * WasteTabbedPanel - Panel izquierdo con pestañas para módulo de residuos
 *
 * @version 3.4
 * @updated 2026-02-27
 * @module core/modules/waste/components/panels
 *
 * @description
 * 14 pestañas: Depósitos, Sitios de descarga, Flota, Zonas, Puntos de servicio,
 * Depósitos sec., Sitios de transferencia, Puntos de abastecimiento, Incidencias,
 * Restricciones, Organizaciones, Equipos, Personas, Rutas.
 * Los 10 tipos mapeables muestran un swatch de color editable en el header del contenido.
 * Los tabs Depósitos y Rutas usan BaseInventoryPanel (nuevo sistema).
 * Los demás tabs aún usan BaseInventoryTab (sistema legacy - pendiente migración).
 * Usa la misma estructura y CSS que todos los demás módulos (tabbed-left-panel.css).
 */

import { useState, useEffect } from 'react';
import {
  Warehouse, ArrowDownToLine, Truck, Hexagon, CircleDot,
  Wrench, ArrowLeftRight, Fuel, AlertTriangle, ShieldBan,
  Building2, UsersRound, UserRoundCog, Route, Network, Trash2,
} from 'lucide-react';
import { BaseInventoryTab } from '../../../../layouts/panels/LeftPanel/BaseInventoryTab';
import { LeftPanelToggle } from '../../../../layouts/panels/LeftPanel/LeftPanel';
import { useRightPanel } from '../../../../layouts/panels/RightPanel/RightPanelContext';
import { useMapTools } from '../../../../map-tools';
import { useWasteElementColorsStore } from '../../store/element-colors-store';
import { MAPPABLE_ELEMENT_IDS } from '../../constants/element-colors';
import type { WasteElementId } from '../../constants/element-colors';
import { RoutesPanel } from './RoutesPanel';
import { DepotsPanel } from './DepotsPanel';
import { ContainersPanel } from './ContainersPanel';
import { WasteAssociationProblemsPanel } from '../WasteAssociationProblemsPanel';
import { WasteImportWizardModal } from '../../import-wizard';
import { mapEngine } from '../../../../map-engine/MapEngineService';
import { useProject } from '../../../../projects';
import { useWasteAssociationStatesLayer } from '../../hooks';
import '../../../../layouts/panels/LeftPanel/tabbed-left-panel.css';
import './waste-tabbed-panel.css';

type WasteTabType =
  | 'containers'
  | WasteElementId
  | 'fleet'
  | 'organizations'
  | 'crews'
  | 'persons'
  | 'routes'
  | 'associations';

interface TabInfo {
  id: WasteTabType;
  icon: React.ReactNode;
  tooltip: string;
  title: string;
  emptyMessage: string;
}

const TABS: TabInfo[] = [
  {
    id: 'containers',
    icon: <Trash2 size={14} />,
    tooltip: 'Contenedores\nPuntos de recolección fijos distribuidos en el territorio. Se pueden revisar, seleccionar y operar desde el mapa y el inventario.',
    title: 'Contenedores',
    emptyMessage: 'Sin contenedores registrados',
  },
  {
    id: 'depots',
    icon: <Warehouse size={14} />,
    tooltip: 'Depósitos\nBase principal donde se guardan vehículos y comienza o finaliza la operación diaria. Punto de origen y retorno de los servicios.',
    title: 'Depósitos',
    emptyMessage: 'Sin depósitos registrados',
  },
  {
    id: 'discharge-sites',
    icon: <ArrowDownToLine size={14} />,
    tooltip: 'Sitios de descarga\nSitios finales de disposición de materiales o descargas operativas. Cierran el ciclo de cada recorrido.',
    title: 'Sitios de descarga',
    emptyMessage: 'Sin sitios de descarga registrados',
  },
  {
    id: 'fleet',
    icon: <Truck size={14} />,
    tooltip: 'Flota\nVehículos disponibles para la operación. Determinan capacidad, alcance y cantidad de servicios posibles.',
    title: 'Flota',
    emptyMessage: 'Sin vehículos registrados',
  },
  {
    id: 'zones',
    icon: <Hexagon size={14} />,
    tooltip: 'Zonas\nSectores territoriales que agrupan demanda o tareas. Unidad básica para planificar cobertura y asignar recursos.',
    title: 'Zonas',
    emptyMessage: 'Sin zonas definidas',
  },
  {
    id: 'service-points',
    icon: <CircleDot size={14} />,
    tooltip: 'Puntos de servicio\nUbicaciones específicas donde se presta el servicio o se realiza una intervención. Elementos operativos dentro de cada zona.',
    title: 'Puntos de servicio',
    emptyMessage: 'Sin puntos de servicio registrados',
  },
  {
    id: 'secondary-depots',
    icon: <Wrench size={14} />,
    tooltip: 'Depósitos secundarios\nInstalaciones de apoyo operativo como talleres, cocheras secundarias o puntos técnicos de mantenimiento.',
    title: 'Depósitos secundarios',
    emptyMessage: 'Sin depósitos secundarios registrados',
  },
  {
    id: 'transfer-sites',
    icon: <ArrowLeftRight size={14} />,
    tooltip: 'Sitios de transferencia\nPuntos intermedios donde se redistribuyen materiales o cargas antes de su destino final.',
    title: 'Sitios de transferencia',
    emptyMessage: 'Sin sitios de transferencia registrados',
  },
  {
    id: 'supply-points',
    icon: <Fuel size={14} />,
    tooltip: 'Puntos de abastecimiento\nLugares de abastecimiento de insumos o energía necesarios para la operación (combustible, carga eléctrica, agua, etc.).',
    title: 'Puntos de abastecimiento',
    emptyMessage: 'Sin puntos de abastecimiento registrados',
  },
  {
    id: 'incidents',
    icon: <AlertTriangle size={14} />,
    tooltip: 'Incidencias\nPuntos con eventos, reclamos o situaciones críticas que requieren atención o seguimiento.',
    title: 'Incidencias',
    emptyMessage: 'Sin incidencias registradas',
  },
  {
    id: 'restrictions',
    icon: <ShieldBan size={14} />,
    tooltip: 'Restricciones\nÁreas o tramos con limitaciones operativas que condicionan circulación, acceso o intervención.',
    title: 'Restricciones',
    emptyMessage: 'Sin restricciones definidas',
  },
  {
    id: 'organizations',
    icon: <Building2 size={14} />,
    tooltip: 'Organizaciones\nEntidades responsables de la prestación del servicio o la gestión de activos dentro del sistema.',
    title: 'Organizaciones',
    emptyMessage: 'Sin organizaciones registradas',
  },
  {
    id: 'crews',
    icon: <UsersRound size={14} />,
    tooltip: 'Equipos\nEquipos de trabajo asignables a tareas, recorridos o intervenciones específicas.',
    title: 'Equipos',
    emptyMessage: 'Sin equipos registrados',
  },
  {
    id: 'persons',
    icon: <UserRoundCog size={14} />,
    tooltip: 'Personas\nPersonas individuales con roles operativos o administrativos dentro del sistema.',
    title: 'Personas',
    emptyMessage: 'Sin personas registradas',
  },
  {
    id: 'routes',
    icon: <Route size={14} />,
    tooltip: 'Rutas\nRecorridos planificados de recolección con contenedores asignados. Trayectorias optimizadas para la operación diaria.',
    title: 'Rutas',
    emptyMessage: 'Sin rutas registradas',
  },
  {
    id: 'associations',
    icon: <Network size={14} />,
    tooltip: 'Asociaciones\nProblemas de asociación punto-red. Revisa y corrige contenedores, depósitos y microbasurales que requieren atención.',
    title: 'Asociaciones',
    emptyMessage: 'Sin problemas de asociación',
  },
];

export function WasteTabbedPanel(): JSX.Element {
  const [activeTab, setActiveTab] = useState<WasteTabType>('depots');
  const [associationLayerVisible, setAssociationLayerVisible] = useState(true);
  const [selectedAssociationEntityId, setSelectedAssociationEntityId] = useState<string | null>(null);
  const [isImportWizardOpen, setIsImportWizardOpen] = useState(false);
  const [preferredImportEntityType, setPreferredImportEntityType] = useState<string | null>(null);
  const { canChangeContent } = useRightPanel();
  const { selectElement, getSelectedElement } = useMapTools();
  const getColor = useWasteElementColorsStore((s) => s.getColor);
  const setColor = useWasteElementColorsStore((s) => s.setColor);
  const { projectId } = useProject();

  // Capa de estados: visible si tab activo Y toggle activado
  const isAssociationsTabActive = activeTab === 'associations';
  const showAssociationLayer = isAssociationsTabActive && associationLayerVisible;
  const { refresh: refreshAssociationLayer } = useWasteAssociationStatesLayer({
    visible: showAssociationLayer,
    selectedEntityId: selectedAssociationEntityId,
  });

  const activeTabInfo = TABS.find(t => t.id === activeTab)!;
  const isMappable = MAPPABLE_ELEMENT_IDS.has(activeTab);
  const selectionKey = `waste-${activeTab}`;
  const activeElementId = getSelectedElement(selectionKey);

  /**
   * Maneja la selección de una entidad desde el panel de problemas de asociación
   * Centra el mapa en las coordenadas de la entidad
   */
  const handleSelectAssociationEntity = (
    entityType: string,
    entityId: string,
    coordinates: [number, number],
  ): void => {
    console.log(`[WasteTabbedPanel] Seleccionando entidad ${entityType}:${entityId} en [${coordinates}]`);

    // Highlight en mapa
    setSelectedAssociationEntityId(entityId);

    const map = mapEngine.getMap();
    if (map) {
      map.flyTo({
        center: coordinates,
        zoom: 18,
        duration: 1000,
      });
    }

    const eventName = entityType === 'waste_container'
      ? 'waste-container-select'
      : entityType === 'waste_depot'
        ? 'waste-depot-select'
        : entityType === 'waste_hotspot'
          ? 'waste-hotspot-select'
          : `waste-${entityType}-select`;

    window.dispatchEvent(new CustomEvent(eventName, {
      detail: { elementId: entityId, entityType, coordinates },
    }));
  };

  /**
   * Escucha evento para abrir tab de rutas desde el toolbar
   */
  useEffect(() => {
    const handleOpenRoutesTab = (): void => {
      console.log('[WasteTabbedPanel] Evento recibido: waste-open-routes-tab');
      setActiveTab('routes');
    };

    const handleOpenImportWizard = (event: Event): void => {
      const detail = (event as CustomEvent<{ entityType?: string }>).detail;
      console.log('[WasteTabbedPanel] Evento recibido: waste-open-import-wizard');
      setPreferredImportEntityType(detail?.entityType ?? null);
      setIsImportWizardOpen(true);
    };

    window.addEventListener('waste-open-routes-tab', handleOpenRoutesTab);
    window.addEventListener('waste-open-import-wizard', handleOpenImportWizard);
    return () => {
      window.removeEventListener('waste-open-routes-tab', handleOpenRoutesTab);
      window.removeEventListener('waste-open-import-wizard', handleOpenImportWizard);
    };
  }, []);

  useEffect(() => {
    const handleContainerSelect = (event: Event): void => {
      const detail = (event as CustomEvent<{ containerId?: string; elementId?: string }>).detail;
      const containerId = detail?.containerId ?? detail?.elementId;

      setActiveTab('containers');
      if (containerId) {
        selectElement('waste-containers', containerId);
      }
    };

    const handleContainerCreated = (): void => {
      setActiveTab('containers');
    };

    window.addEventListener('waste-container-select', handleContainerSelect);
    window.addEventListener('waste-containers-select', handleContainerSelect);
    window.addEventListener('container-created', handleContainerCreated);

    return () => {
      window.removeEventListener('waste-container-select', handleContainerSelect);
      window.removeEventListener('waste-containers-select', handleContainerSelect);
      window.removeEventListener('container-created', handleContainerCreated);
    };
  }, [selectElement]);

  /**
   * Maneja el clic en el código de un elemento
   *
   * @remarks
   * Antes de cambiar de elemento, verifica si hay cambios sin guardar en el panel derecho.
   * Si hay cambios, pregunta al usuario si desea descartarlos.
   * Solo cambia de elemento si el usuario confirma o no hay cambios.
   *
   * Despacha un evento personalizado que puede ser manejado por:
   * - MapLayout para selección y apertura del panel derecho
   * - Integración con el sistema de selección del módulo
   */
  const handleCodeClick = async (elementId: string): Promise<void> => {
    console.log('[WasteTabbedPanel] ====================================');
    console.log(`[WasteTabbedPanel] handleCodeClick - Elemento clickeado: ${activeTab} - ${elementId}`);

    // Verificar si hay cambios sin guardar ANTES de cambiar
    console.log('[WasteTabbedPanel] Verificando cambios sin guardar...');
    const canChange = await canChangeContent();

    if (!canChange) {
      console.warn('[WasteTabbedPanel] ✗ Usuario canceló el cambio (hay cambios sin guardar)');
      console.log('[WasteTabbedPanel] ====================================');
      return; // Usuario canceló, no cambiar
    }

    console.log('[WasteTabbedPanel] ✓ Cambios verificados, procediendo con el cambio...');

    // Marcar elemento como seleccionado para resaltado en sidebar
    selectElement(selectionKey, elementId);

    console.log(`[WasteTabbedPanel] Despachando evento: waste-${activeTab}-select`);

    // Despachar evento para que otros componentes puedan reaccionar
    window.dispatchEvent(new CustomEvent(`waste-${activeTab}-select`, {
      detail: { elementId, elementType: activeTab },
    }));

    console.log('[WasteTabbedPanel] ====================================');
  };

  return (
    <div className="waste-tabbed-panel tabbed-left-panel">
      <WasteImportWizardModal
        isOpen={isImportWizardOpen}
        projectId={projectId}
        preferredEntityType={preferredImportEntityType}
        onClose={() => {
          setIsImportWizardOpen(false);
          setPreferredImportEntityType(null);
        }}
      />

      <div className="tabs-header">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            title={tab.title}
            aria-label={tab.title}
          >
            {tab.icon}
          </button>
        ))}
      </div>

      <div className="content-card-wrapper">
        <div className="content-card">
          <div className="tab-content">
            {activeTab === 'routes' ? (
              <RoutesPanel />
            ) : activeTab === 'containers' ? (
              <ContainersPanel />
            ) : activeTab === 'depots' ? (
              <DepotsPanel />
            ) : activeTab === 'associations' ? (
              <WasteAssociationProblemsPanel
                projectId={projectId}
                onSelectEntity={handleSelectAssociationEntity}
                onRefreshNeeded={refreshAssociationLayer}
                layerVisible={associationLayerVisible}
                onToggleLayer={setAssociationLayerVisible}
              />
            ) : (
              <BaseInventoryTab
                key={activeTab}
                title={activeTabInfo.title}
                emptyMessage={activeTabInfo.emptyMessage}
                eventPrefix={`waste-${activeTab}`}
                moduleId="waste"
                elementColor={isMappable ? getColor(activeTab as WasteElementId) : undefined}
                onElementColorChange={isMappable ? (hex) => setColor(activeTab as WasteElementId, hex) : undefined}
                onCodeClick={handleCodeClick}
                activeElementId={activeElementId}
              />
            )}
          </div>
          <LeftPanelToggle />
        </div>
      </div>
    </div>
  );
}
